import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GitBranch, Plus, Edit, Trash2, Search, RefreshCw, 
  AlertCircle, X, Check, XCircle, MapPin, User, Phone, Mail
} from 'lucide-react';
import { branchesAPI } from '../../services/api';
import { useBranch } from '../../context/BranchContext';

const BranchesManagement = () => {
  const { refreshBranches } = useBranch();
  const weekDays = [
    { key: 'saturday', label: 'السبت' },
    { key: 'sunday', label: 'الأحد' },
    { key: 'monday', label: 'الاثنين' },
    { key: 'tuesday', label: 'الثلاثاء' },
    { key: 'wednesday', label: 'الأربعاء' },
    { key: 'thursday', label: 'الخميس' },
    { key: 'friday', label: 'الجمعة' },
  ];
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    branch_name: '',
    supervisor_name: '',
    supervisor_email: '',
    supervisor_mobile: '',
    city: '',
    address: '',
    active: true,
    home_visit_enabled: false,
    home_visit_days: [],
    home_visit_start_time: '13:00',
    home_visit_end_time: '17:00'
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Cities list
  const cities = [
    'الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام',
    'الخبر', 'الظهران', 'تبوك', 'أبها', 'الطائف', 'بريدة', 'نجران',
    'جازان', 'حائل', 'الجبيل', 'ينبع', 'خميس مشيط', 'القطيف'
  ];

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await branchesAPI.getAll({ limit: 100 });
      // paginatedResponse returns: { data: { items, total, page, limit } }
      const data = response.data?.data?.items || response.data?.items || response.data?.data || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('حدث خطأ في تحميل البيانات');
      setBranches([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);

  const resetForm = () => {
    setFormData({
      branch_name: '',
      supervisor_name: '',
      supervisor_email: '',
      supervisor_mobile: '',
      city: '',
      address: '',
      active: true,
      home_visit_enabled: false,
      home_visit_days: [],
      home_visit_start_time: '13:00',
      home_visit_end_time: '17:00'
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.branch_name.trim()) errors.branch_name = 'اسم الفرع مطلوب';
    if (!formData.supervisor_name.trim()) errors.supervisor_name = 'اسم المشرف مطلوب';
    if (!formData.supervisor_email.trim()) {
      errors.supervisor_email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.supervisor_email)) {
      errors.supervisor_email = 'البريد الإلكتروني غير صحيح';
    }
    if (!formData.supervisor_mobile.trim()) {
      errors.supervisor_mobile = 'رقم الجوال مطلوب';
    } else if (!/^05\d{8}$/.test(formData.supervisor_mobile.replace(/\s/g, ''))) {
      errors.supervisor_mobile = 'رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام';
    }
    if (!formData.city) errors.city = 'المدينة مطلوبة';
    if (!formData.address.trim()) errors.address = 'العنوان مطلوب';
    if (formData.home_visit_enabled) {
      if (!Array.isArray(formData.home_visit_days) || formData.home_visit_days.length === 0) {
        errors.home_visit_days = 'اختر يوم عمل واحد على الأقل للزيارات المنزلية';
      }
      if (!formData.home_visit_start_time || !formData.home_visit_end_time) {
        errors.home_visit_time = 'حدد وقت البداية والنهاية للزيارات المنزلية';
      } else if (formData.home_visit_start_time >= formData.home_visit_end_time) {
        errors.home_visit_time = 'وقت النهاية يجب أن يكون بعد وقت البداية';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (branch) => {
    setSelectedBranch(branch);
    setFormData({
      branch_name: branch.branch_name || '',
      supervisor_name: branch.supervisor_name || '',
      supervisor_email: branch.supervisor_email || '',
      supervisor_mobile: branch.supervisor_mobile || '',
      city: branch.city || '',
      address: branch.address || '',
      active: branch.active !== false,
      home_visit_enabled: branch.home_visit_enabled === true,
      home_visit_days: Array.isArray(branch.home_visit_schedule)
        ? branch.home_visit_schedule.map((slot) => slot.day_of_week)
        : [],
      home_visit_start_time: branch.home_visit_schedule?.[0]?.start_time || '13:00',
      home_visit_end_time: branch.home_visit_schedule?.[0]?.end_time || '17:00'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        home_visit_schedule: formData.home_visit_enabled
          ? formData.home_visit_days.map((day) => ({
              day_of_week: day,
              start_time: formData.home_visit_start_time,
              end_time: formData.home_visit_end_time
            }))
          : []
      };

      if (showEditModal && selectedBranch) {
        await branchesAPI.update(selectedBranch._id, payload);
      } else {
        await branchesAPI.create(payload);
      }
      await fetchBranches();
      await refreshBranches();
      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedBranch) return;
    setDeleting(true);
    try {
      await branchesAPI.delete(selectedBranch._id);
      setBranches(prev => prev.filter(b => b._id !== selectedBranch._id));
      await refreshBranches();
      setShowDeleteModal(false);
      setSelectedBranch(null);
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحذف');
    }
    setDeleting(false);
  };

  const filteredBranches = branches.filter(b => 
    b.branch_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.supervisor_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const inputClasses = (fieldName) => `w-full px-4 py-2.5 bg-white border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
    formErrors[fieldName] 
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500'
  }`;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
            <GitBranch className="w-6 h-6 text-slate-100" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">إدارة فروع المختبر</h1>
            <p className="text-slate-500 text-sm mt-1">Lab Branches Management</p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleAdd}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          <span>إضافة فرع جديد</span>
        </motion.button>
      </motion.div>

      {/* Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="البحث بالاسم أو المدينة أو المشرف..."
              className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          <button onClick={fetchBranches} className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3 text-amber-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="mr-auto"><X className="w-4 h-4" /></button>
        </motion.div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filteredBranches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <GitBranch className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">لا يوجد فروع</p>
            <button onClick={handleAdd} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl">إضافة فرع</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">اسم الفرع</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">المدينة</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">المشرف</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الجوال</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الزيارات المنزلية</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredBranches.map((branch, index) => (
                  <motion.tr
                    key={branch._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                          <GitBranch className="w-4 h-4 text-slate-600" strokeWidth={2} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{branch.branch_name}</p>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{branch.address}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2 text-slate-700">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                        <span className="text-sm">{branch.city}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2 text-slate-700">
                        <User className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{branch.supervisor_name}</p>
                          <p className="text-xs text-slate-500" dir="ltr">{branch.supervisor_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2 text-slate-700">
                        <Phone className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                        <span className="font-mono text-sm" dir="ltr">{branch.supervisor_mobile}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide border ${
                        branch.active 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${branch.active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        {branch.active ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide border ${
                        branch.home_visit_enabled
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200'
                      }`}>
                        {branch.home_visit_enabled ? 'مفعل' : 'غير مفعل'}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEdit(branch)}
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => { setSelectedBranch(branch); setShowDeleteModal(true); }}
                          className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <GitBranch className="w-5 h-5" />
                  {showEditModal ? 'تعديل الفرع' : 'إضافة فرع جديد'}
                </h3>
                <button onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="text-slate-400 hover:text-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Branch Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم الفرع <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.branch_name}
                    onChange={e => setFormData({...formData, branch_name: e.target.value})}
                    className={inputClasses('branch_name')}
                    placeholder="مثال: الفرع الرئيسي"
                  />
                  {formErrors.branch_name && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.branch_name}</p>
                  )}
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    المدينة <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.city}
                    onChange={e => setFormData({...formData, city: e.target.value})}
                    className={inputClasses('city')}
                  >
                    <option value="">اختر المدينة</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                  {formErrors.city && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.city}</p>
                  )}
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    العنوان التفصيلي <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={e => setFormData({...formData, address: e.target.value})}
                    className={inputClasses('address')}
                    rows={2}
                    placeholder="الحي، الشارع، رقم المبنى"
                  />
                  {formErrors.address && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.address}</p>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-4 mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">بيانات المشرف</p>
                </div>

                {/* Supervisor Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم المشرف <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.supervisor_name}
                    onChange={e => setFormData({...formData, supervisor_name: e.target.value})}
                    className={inputClasses('supervisor_name')}
                    placeholder="الاسم الكامل"
                  />
                  {formErrors.supervisor_name && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.supervisor_name}</p>
                  )}
                </div>

                {/* Supervisor Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    البريد الإلكتروني <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.supervisor_email}
                    onChange={e => setFormData({...formData, supervisor_email: e.target.value})}
                    className={inputClasses('supervisor_email')}
                    placeholder="example@lab.com"
                    dir="ltr"
                  />
                  {formErrors.supervisor_email && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.supervisor_email}</p>
                  )}
                </div>

                {/* Supervisor Mobile */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رقم الجوال <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.supervisor_mobile}
                    onChange={e => setFormData({...formData, supervisor_mobile: e.target.value})}
                    className={inputClasses('supervisor_mobile')}
                    placeholder="05xxxxxxxx"
                    dir="ltr"
                  />
                  {formErrors.supervisor_mobile && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.supervisor_mobile}</p>
                  )}
                </div>

                {/* Active Toggle */}
                <div className="flex items-center gap-3 pt-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={e => setFormData({...formData, active: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm text-gray-700">الفرع نشط</span>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.home_visit_enabled}
                      onChange={e => setFormData({
                        ...formData,
                        home_visit_enabled: e.target.checked,
                        home_visit_days: e.target.checked ? formData.home_visit_days : []
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm text-gray-700">استقبال الزيارات المنزلية لهذا الفرع</span>
                </div>

                {formData.home_visit_enabled && (
                  <div className="space-y-3 rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                    <p className="text-sm font-medium text-blue-800">أيام وساعات استقبال الزيارات المنزلية</p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {weekDays.map((day) => {
                        const checked = formData.home_visit_days.includes(day.key);
                        return (
                          <label key={day.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${checked ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-gray-200 text-gray-700'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const nextDays = e.target.checked
                                  ? [...formData.home_visit_days, day.key]
                                  : formData.home_visit_days.filter((d) => d !== day.key);
                                setFormData({ ...formData, home_visit_days: nextDays });
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>{day.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    {formErrors.home_visit_days && (
                      <p className="text-xs text-red-500">{formErrors.home_visit_days}</p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">من الساعة</label>
                        <input
                          type="time"
                          value={formData.home_visit_start_time}
                          onChange={(e) => setFormData({ ...formData, home_visit_start_time: e.target.value })}
                          className={inputClasses('home_visit_time')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">إلى الساعة</label>
                        <input
                          type="time"
                          value={formData.home_visit_end_time}
                          onChange={(e) => setFormData({ ...formData, home_visit_end_time: e.target.value })}
                          className={inputClasses('home_visit_time')}
                        />
                      </div>
                    </div>
                    {formErrors.home_visit_time && (
                      <p className="text-xs text-red-500">{formErrors.home_visit_time}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</>
                  ) : (
                    showEditModal ? 'حفظ التغييرات' : 'إضافة الفرع'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">حذف الفرع</h3>
                <p className="text-gray-500 mb-6">
                  هل أنت متأكد من حذف فرع "{selectedBranch?.branch_name}"؟
                  <br />
                  <span className="text-sm text-red-500">هذا الإجراء لا يمكن التراجع عنه</span>
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">إلغاء</button>
                  <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحذف...</> : 'حذف'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default BranchesManagement;
